import { InlineSelect, Pagination } from '@design-system';
import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import React, { useContext, useEffect, useMemo, useState } from 'react';

import {
  ARTIST_INDEX_PAGE_TYPE,
  ARTISTS_PER_PAGE,
  PLACE_HOLDER_IMAGE,
} from '../../constants';
import { AuthEntryPoint } from '../../features/auth/types';
import { pathFor } from '../../helpers/links';
import { useIsSubscribed } from '../../hooks/subscription';
import { useArtistFilters } from '../../hooks/useArtistFilters';
import { useAuthenticate } from '../../hooks/useAuthenticate';
import { usePathname } from '../../hooks/usePathname';
import { AccountContext } from '../../providers/AccountProvider';
import type { AugmentedArtistCard } from '../../types/Artists';
import type { ArtistOrderSort } from '../../types/Filters';
import { GeneratedSubscriptionType } from '../../types/generated';
import {
  TrackArtistClicked,
  TrackArtistsResults,
  TrackFiltersClicked,
} from '../../utils/analytics';
import { getArtistTileOverline } from '../../utils/artists';
import { ArtistFilterer } from '../../utils/filtering';
import { ArtistProfileCard } from '../ArtistProfileCard/ArtistProfileCard';
import { NavigationStateContext } from '../Containers/NavigationStateProvider/NavigationStateProvider';
import FullpageCta from '../Content/FullpageCTA';
import ArtistFiltersBar from '../Filters/ArtistFiltersBar';
import { OpenFiltersButton } from '../Filters/OpenFiltersButton';
import PageHeading from '../Layout/PageHeading';
import PageSection from '../Layout/PageSection';
import { SearchBar } from '../Search/SearchBar';

const ArtistFiltersDrawer = dynamic(
  () => import('../Filters/ArtistFiltersDrawer'),
  { ssr: false },
);

type Props = {
  blok: ArtistsPageType;
};

const Artists = ({
  blok: { artists, artistsPageByline, artistsPageHeading, pageNumber },
}: Props) => {
  const { t } = useTranslation(['artists', 'filters', 'authentication']);
  const { authenticateAndSubscribe } = useAuthenticate();

  const [currentPage, setCurrentPage] = useState(pageNumber);
  const [isFilterPaneShown, setIsFilterPaneShown] = useState(false);
  const [paginationQuery, setPaginationQuery] = useState('');
  const { clearNavigationState, setNavigationState } = useContext(
    NavigationStateContext,
  );
  const [isOrderInteractedWith, setIsOrderInteractedWith] =
    useState<boolean>(false);
  const { onboarding } = useContext(AccountContext);

  useEffect(() => {
    setCurrentPage(pageNumber);
  }, [pageNumber]);

  const pathname = usePathname();

  const filters = useArtistFilters();
  const filterer = useMemo(() => {
    return new ArtistFilterer(artists);
  }, [artists]);

  const [filteredArtists, setFilteredArtists] = useState(
    artists.filter((a) => !!a.name),
  );

  const pathSubstring = pathname.substring(pathname.lastIndexOf('/') + 1);

  let page = 1;
  try {
    page = parseInt(pathSubstring, 10);
  } catch (error) {
    // do nothing and keep 1 as page
  }

  useEffect(() => {
    clearNavigationState();
    setNavigationState({
      type: 'ARTIST',
      order: filters.order.value,
      query: filters.searchTerm.value,
      mediums: filters.mediums.values,
      categories: filters.categories.values,
      count: filteredArtists.length,
      page,
    });
  }, [
    filters.mediums.values.join(','),
    filters.categories.values.join(','),
    filters.searchTerm.value,
    filters.order.value,
    filteredArtists,
    setNavigationState,
    clearNavigationState,
  ]);

  useEffect(() => {
    if (isOrderInteractedWith) {
      TrackArtistsResults({
        page: ARTIST_INDEX_PAGE_TYPE,
        pageNumber: currentPage,
        searchTerm: filters.searchTerm.value,
        categoryFilter: filters.categories.values,
        mediumFilter: filters.mediums.values,
        orderFilter: filters.order.value,
        resultCount: artistsToDisplay.length,
      });
    }
  }, [filters.order.value?.toString()]);

  useEffect(() => {
    if (filters.isInteractedWith) {
      TrackArtistsResults({
        page: ARTIST_INDEX_PAGE_TYPE,
        pageNumber: currentPage,
        searchTerm: filters.searchTerm.value,
        categoryFilter: filters.categories.values,
        mediumFilter: filters.mediums.values,
        orderFilter: filters.order.value,
        resultCount: artistsToDisplay.length,
      });
    }
  }, [
    filters.categories.values.join(','),
    filters.mediums.values.join(','),
    filters.order.value,
    currentPage,
  ]);

  useEffect(() => {
    if (filters.searchTerm.value) {
      TrackArtistsResults({
        page: ARTIST_INDEX_PAGE_TYPE,
        pageNumber: currentPage,
        searchTerm: filters.searchTerm.value,
        categoryFilter: filters.categories.values,
        mediumFilter: filters.mediums.values,
        orderFilter: filters.order.value,
        resultCount: artistsToDisplay.length,
      });
    }
  }, [filters.searchTerm.value]);

  useEffect(() => {
    setFilteredArtists(
      filterer.filter({
        categories: filters.categories.values,
        mediums: filters.mediums.values,
        searchTerm: filters.searchTerm.value,
      }),
    );

    setPaginationQuery(
      window.location.search.split('?')[1]
        ? `?${window.location.search.split('?')[1]}`
        : '',
    );

    if (filters.isInteractedWith) {
      setCurrentPage(1);
    }
  }, [
    filters.categories.values.join(','),
    filters.mediums.values.join(','),
    filters.searchTerm.value,
    filters.order.value,
  ]);

  const sortedArtists = useMemo(
    () => sortArtists(filteredArtists, filters.order.value),
    [filteredArtists, filters.order.value?.toString()],
  );

  const artistsToDisplay = useMemo(() => {
    const indexableArtists = sortedArtists?.filter(
      (art) => !art.excludeFromIndex,
    );
    return getPage(indexableArtists, currentPage, ARTISTS_PER_PAGE);
  }, [sortedArtists, currentPage]);

  const totalPages = useMemo(
    () => Math.ceil(filteredArtists.length / ARTISTS_PER_PAGE),
    [filteredArtists],
  );

  // to-do: This has became the empty state, not sure it's a bug
  // const shouldShowCta = useMemo(() => {
  //   if (totalPages > 1) return undefined;
  //   if (totalPages === 1 && filters.searchTerm.value) return undefined;

  //   return true;
  // }, [filters.searchTerm.value, totalPages]);

  const { checkIsSubscribed, refetch } = useIsSubscribed(
    artistsToDisplay.map((artist) => ({
      type: GeneratedSubscriptionType.Artist,
      objectId: artist.internalId || '',
    })),
  );

  const activeFiltersCount =
    filters.categories.values.length + filters.mediums.values.length;

  return (
    <main className="my-9 space-y-9 md:my-12 md:space-y-12">
      <div className="relative space-y-2">
        <PageSection>
          <div className="flex w-full flex-1 flex-col space-y-6">
            <PageHeading
              heading={artistsPageHeading}
              strapline={artistsPageByline}
            />
            <div className="flex w-full flex-1 space-x-3">
              <div className="flex-1">
                <SearchBar
                  defaultValue={filters.searchTerm.value}
                  onChange={filters.searchTerm.set}
                  placeholder={t('searchPlaceholder')}
                />
              </div>
              <div className="shrink-0">
                <OpenFiltersButton
                  onOpen={() => {
                    setIsFilterPaneShown(true);
                    TrackFiltersClicked({
                      page: 'artists index',
                      indexCategoryFilter: filters.categories.values,
                      indexMediumFilter: filters.mediums.values,
                      indexOrderFilter: filters.order.value,
                      indexSearchQuery: filters.searchTerm.value,
                      indexLength: filteredArtists.length,
                    });
                  }}
                />
              </div>
            </div>
            <ArtistFiltersDrawer
              artistCount={filteredArtists.length}
              filters={filters}
              setShown={setIsFilterPaneShown}
              shown={isFilterPaneShown}
            />
          </div>
        </PageSection>

        {activeFiltersCount > 0 && (
          <PageSection fullWidth>
            <ArtistFiltersBar filters={filters} />
          </PageSection>
        )}
      </div>

      <PageSection>
        <div className="space-y-6">
          {artistsToDisplay.length > 0 && (
            <section className="flex items-center justify-between space-x-3">
              <div className="shrink-0">
                <span className="text-neutral-4">
                  {t('artistsCount', { count: filteredArtists.length })}
                </span>
              </div>
              <div className="shrink-0">
                <InlineSelect
                  onChange={(e) => {
                    filters.order.set(e.currentTarget.value as ArtistOrderSort);
                    setIsOrderInteractedWith(true);
                  }}
                  options={[
                    {
                      key: t('filters:artists.sortRelevance'),
                      value: 'Relevance',
                    },
                    {
                      key: t('filters:artists.sortAlphabetical'),
                      value: 'Alphabetical',
                    },
                    {
                      key: t('filters:artists.sortNumCollaborations'),
                      value: 'NumCollaborations',
                    },
                    {
                      key: t('filters:artists.sortReleaseDate'),
                      value: 'ReleaseDate',
                    },
                  ]}
                  textAlign="right"
                  value={filters.order.value}
                />
              </div>
            </section>
          )}

          <section className="grid-section gap-y-6">
            {artistsToDisplay.length > 0 ? (
              <>
                {artistsToDisplay.map(
                  (
                    {
                      alt,
                      focus,
                      galleryImages,
                      hasUpcomingRelease,
                      hideCollaborations,
                      image,
                      internalId,
                      link,
                      name,
                      totalWorks,
                      uuid,
                    },
                    index,
                  ) => (
                    <article
                      key={uuid}
                      className="col-span-6 md:col-span-4 lg:col-span-3"
                    >
                      <ArtistProfileCard
                        href={`/${link}`}
                        image={{
                          alt: alt || name,
                          focus: focus || '',
                          eagerLoading: index < 5,
                          hoverSrc: galleryImages?.[0],
                          src: image || PLACE_HOLDER_IMAGE,
                        }}
                        isSubscribed={checkIsSubscribed(
                          internalId,
                          GeneratedSubscriptionType.Artist,
                        )}
                        name={name}
                        onClick={() => {
                          TrackArtistClicked({
                            page: ARTIST_INDEX_PAGE_TYPE,
                            context: 'artist',
                            id: uuid,
                            artistName: name,
                            artistUrl: link,
                            position: index,
                            searchTerm: filters.searchTerm.value,
                            categoryFilter: filters.categories.values,
                            mediumFilter: filters.mediums.values,
                            orderFilter: filters.order.value,
                            resultCount: artistsToDisplay.length,
                          });
                        }}
                        onClickSubscribe={async () => {
                          await authenticateAndSubscribe({
                            authScreenThumbnail: image,
                            authScreenTitle: t(
                              'authentication:dynamicTitle.artist',
                            ),
                            subscriptions: [
                              {
                                objectId: internalId ?? '',
                                type: GeneratedSubscriptionType.Artist,
                                name,
                                ref: '',
                              },
                            ],
                            analyticsProperties: {
                              source: AuthEntryPoint.Artist,
                              'button_location': 'artist index tile',
                              'artist name': name,
                              artist_id: internalId || '',
                            },
                            showOnboarding: true,
                            hideSuccessScreen: !onboarding?.isOnboardingStarted, // if onboarding is not started then hide success screen after profile step
                            onFlowComplete: () => {
                              onboarding?.show({
                                page: AuthEntryPoint.Artist,
                                id: internalId || '',
                              });
                            },
                          });

                          refetch();
                        }}
                        overline={
                          hasUpcomingRelease
                            ? t('artistUpcomingRelease')
                            : undefined
                        }
                        subtitle={getArtistTileOverline(
                          totalWorks,
                          hideCollaborations,
                          t,
                        )}
                      />
                    </article>
                  ),
                )}
              </>
            ) : (
              <div className="col-span-12">
                <p className="text-neutral-4">{t('noResultsBody')}</p>
              </div>
            )}

            {totalPages > 1 && (
              <div className="col-span-12 pt-3 md:pt-6">
                <Pagination
                  currentPage={currentPage}
                  numPages={totalPages}
                  paginationUrl={pathFor({
                    page: 'ARTISTS_INDEX_ROOT_PATH',
                  })}
                  queryParams={paginationQuery}
                />
              </div>
            )}
          </section>
        </div>
      </PageSection>

      {artistsToDisplay.length === 0 && (
        <PageSection>
          <FullpageCta body={t('ctaBody')} title={t('ctaHeader')} />
        </PageSection>
      )}
    </main>
  );
};

export default Artists;

function getPage<T>(resources: T[], page: number, perPage: number): T[] {
  const start = page - 1;
  const end = page;

  return resources.slice(start * perPage, end * perPage);
}

const sortByPriority = (
  artists: AugmentedArtistCard[],
  priorityList: string[],
  depriorityList: string[],
) => {
  return artists.sort((a, b) => {
    const indexA = priorityList.indexOf(a.slug);
    const indexB = priorityList.indexOf(b.slug);

    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }
    if (indexA !== -1) {
      return -1;
    }
    if (indexB !== -1) {
      return 1;
    }

    const deindexA = depriorityList.indexOf(a.slug);
    const deindexB = depriorityList.indexOf(b.slug);
    if (deindexA !== -1 && deindexB !== -1) {
      return deindexA - deindexB;
    }
    if (deindexA !== -1) {
      return 1;
    }
    if (deindexB !== -1) {
      return -1;
    }

    return b.totalWorks - a.totalWorks;
  });
};

const priorityList = [
  'shirin-neshat',
  'danielle-mckinney',
  'lee-ufan',
  'robert-longo',
  'jenny-holzer',
  'anish-kapoor',
  'grayson-perry',
  'mark-bradford',
  'carrie-mae-weems',
  'judy-chicago',
  'ai-weiwei',
  'harland-miller',
  'ed-ruscha',
  'maurizio-catellan',
  'adam-pendleton',
  'paul-mccarthy',
  'cai-guo-qiang',
  'mickalene-thomas',
  'hilary-pecis',
  'larry-bell',
  'elmgreen-and-dragset',
  'claire-tabouret',
  'magdalena-suarez-frimkess',
  'lawrence-weiner',
  'george-condo',
  'michaela-yearwood-dan',
  'tschabalala-self',
  'cindy-sherman',
  'jia-aili',
  'derek-fordjour',
  'huma-bhabha',
  'lina-iris-viktor',
  'jordan-casteel',
  'nina-chanel-abney',
  'tau-lewis',
  'jean-michel-othoniel',
  'tunji-adeniyi-jones',
  'jenna-gribbon',
  'elizabeth-peyton',
  'hank-willis-thomas',
  'dominique-fung',
  'wang-zhibo',
  'george-rouy',
  'lucian-freud',
  'yue-minjun',
  'barry-mcgee',
  'gregory-crewdson',
  'scott-kahn',
  'julian-schnabel',
  'josh-smith',
];

const depriorityList = [
  'a-lei',
  'kita-ayako',
  'ayako-rokkaku',
  'b-wing',
  'beeple',
  'bell-nakai',
  'benrei-huang',
  'chen-wei-ting',
  'claire-silver',
  'deekay',
  'face-oka',
  'giorgiko',
  'hajime-sorayama',
  'haroshi',
  'hebru-brantley',
  'hikari-shimoda',
  'imon-boy',
  'jack-kaido',
  'james-jarvis',
  'james-jean',
  'jang-koal',
  'javier-calleja',
  'kai',
  'katie-hector',
  'kukwon-woo',
  'lin-yen-liang',
  'ly',
  'maiko-kobayashi',
  'makiko-kudo',
  'minhee-kim',
  'nksin',
  'okokume',
  'otherworld',
  'paco-pomet',
  'paul-insect',
  'pejac',
  'roby-dwi-antono',
  'rooo-lou',
  'ryol',
  'satoru-koizumi',
  'so-youn-lee',
  'stickymonger',
  'super-future-kid',
  'takahiro-komuro',
  'takeru-amano',
  'taku-obata',
  'taku-obata',
  'tide',
  'yu-nagaba',
  'yuichi-hirako',
  'yutaka-hashimoto',
];

function sortArtists(
  artists: AugmentedArtistCard[],
  order: ArtistOrderSort,
): AugmentedArtistCard[] {
  switch (order) {
    case 'Alphabetical':
      return artists.sort((a, b) => a.name.localeCompare(b.name));
    case 'NumCollaborations':
      return artists.sort((a, b) => b.totalWorks - a.totalWorks);
    case 'ReleaseDate':
      return artists.sort((a, b) => {
        return a.firstPublishedAt !== null && b.firstPublishedAt !== null
          ? b.firstPublishedAt.localeCompare(a.firstPublishedAt)
          : 0;
      });
    default:
      return sortByPriority(artists, priorityList, depriorityList);
  }
}
